import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import { showBanner } from '../../global-state/banner-slice'
import {
  jobScheduleServices,
  JobScheduleFormData,
  JobScheduleItem,
  JobScheduleUpdateData,
} from '../../services/job-schedule-service'
import { NewsSchedulePresenter } from './news-schedule-form-presenter'

// The news job's key is fixed in code, so the form always edits this job.
export const NEWS_FETCH_JOB_KEY = 'NEWS_FETCH'

const RUN_STATUS_POLL_MS = 3000

const defaultValues: JobScheduleFormData = {
  name: 'News fetch',
  enabled: true,
  frequency: 'DAILY',
  timeOfDay: '09:00',
  timezone: 'Asia/Kolkata',
  daysOfWeek: [],
}

const toFormValues = (item: JobScheduleItem): JobScheduleFormData => ({
  name: item.name,
  enabled: item.enabled,
  frequency: item.frequency,
  timeOfDay: item.timeOfDay,
  timezone: item.timezone,
  daysOfWeek: item.daysOfWeek ?? [],
})

/** Daily schedules send no days. Weekly schedules send their days in order. */
const toUpdateData = (values: JobScheduleFormData): JobScheduleUpdateData => ({
  name: values.name.trim(),
  enabled: values.enabled,
  frequency: values.frequency,
  timeOfDay: values.timeOfDay,
  timezone: values.timezone,
  daysOfWeek: values.frequency === 'WEEKLY' ? [...values.daysOfWeek].sort((a, b) => a - b) : null,
})

function NewsScheduleFormContainer() {
  const dispatch = useDispatch()

  const form = useForm<JobScheduleFormData>({
    defaultValues,
    mode: 'onBlur',
  })

  const [schedule, setSchedule] = useState<JobScheduleItem | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isStarting, setIsStarting] = useState(false)

  // Reloads only the saved schedule, so unsaved form edits are kept.
  const refreshSchedule = async () => {
    const res = await jobScheduleServices.getByJobKey(NEWS_FETCH_JOB_KEY)
    if (res?.data?.item) setSchedule(res.data.item)
  }

  // While a run is in progress, poll until it finishes so the status updates.
  useEffect(() => {
    if (schedule?.lastStatus !== 'RUNNING') return
    const timer = setInterval(refreshSchedule, RUN_STATUS_POLL_MS)
    return () => clearInterval(timer)
  }, [schedule?.lastStatus])

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        setIsLoading(true)
        const res = await jobScheduleServices.getByJobKey(NEWS_FETCH_JOB_KEY)
        if (res?.data?.item) {
          const item: JobScheduleItem = res.data.item
          setSchedule(item)
          form.reset(toFormValues(item))
        }
      } catch (error) {
        console.error(error)
        dispatch(showBanner({ text: 'Failed to fetch news schedule', variant: 'error-banner' }))
      } finally {
        setIsLoading(false)
      }
    }
    fetchSchedule()
  }, [])

  const saveSchedule = async (payload: JobScheduleUpdateData) => {
    const res = await jobScheduleServices.update(NEWS_FETCH_JOB_KEY, payload)

    if (res?.status === 200) {
      setSchedule(res.data.item)
      dispatch(showBanner({ text: 'News schedule saved', variant: 'success-banner' }))
    } else {
      dispatch(
        showBanner({
          text: res?.data?.message || 'Something went wrong! Please try again.',
          variant: 'error-banner',
        }),
      )
    }
  }

  const onSubmit = async (data: JobScheduleFormData) => {
    if (data.frequency === 'WEEKLY' && data.daysOfWeek.length === 0) {
      form.setError('daysOfWeek', { type: 'manual', message: 'Select at least one day' })
      return
    }

    setIsSubmitting(true)
    try {
      await saveSchedule({
        ...toUpdateData(data),
        // A new schedule starts enabled. An existing one keeps its table state.
        enabled: schedule?.enabled ?? defaultValues.enabled,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onToggleEnabled = async (enabled: boolean) => {
    if (!schedule) return
    setIsSubmitting(true)
    try {
      await saveSchedule(toUpdateData({ ...toFormValues(schedule), enabled }))
    } finally {
      setIsSubmitting(false)
    }
  }

  const onEditRecord = () => {
    if (!schedule) return
    form.reset(toFormValues(schedule))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const onRunNow = async () => {
    setIsStarting(true)
    try {
      const res = await jobScheduleServices.runNow(NEWS_FETCH_JOB_KEY)
      if (res?.status === 202) {
        dispatch(showBanner({ text: 'News fetch started', variant: 'success-banner' }))
        await refreshSchedule()
      } else {
        dispatch(
          showBanner({
            text: res?.data?.message || 'Could not start the news fetch. Please try again.',
            variant: 'error-banner',
          }),
        )
      }
    } finally {
      setIsStarting(false)
    }
  }

  return (
    <NewsSchedulePresenter
      form={form}
      schedule={schedule}
      jobKey={NEWS_FETCH_JOB_KEY}
      isSubmitting={isSubmitting}
      isLoading={isLoading}
      isStarting={isStarting}
      onSubmit={onSubmit}
      onToggleEnabled={onToggleEnabled}
      onEditRecord={onEditRecord}
      onRunNow={onRunNow}
    />
  )
}

export { NewsScheduleFormContainer }
